import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { SecondLevelEntry } from '@app/shared/components/filter-input/second-level-entry';
import { FocusableElement } from '@app/shared/directives/autofocus.directive';
import { TippyDirective } from '@app/shared/directives/tippy.directive';
import { FilterableEntry } from '@app/shared/models/filterable-entry';
import { TypeaheadEntry } from '@app/shared/services/typeahead-basket.service';
import { debounce, toMap } from '@app/utils/common';
import { isPositiveParse } from '@app/utils/filter/parser/parse-result';
import { parse } from '@app/utils/filter/parser/parser';
import { Term } from '@app/utils/filter/terms/term';
import { termToString } from '@app/utils/filter/terms/term-to-string';
import { Key } from '@app/utils/key';
import { MetaListCountMode } from '@app/utils/lists/meta-list';
import { MetaDataProperty } from '@app/utils/lists/meta-list/meta-data-property';
import { MetaList } from '@app/utils/lists/meta-list/meta-list';
import { MetaListImpl } from '@app/utils/lists/meta-list/meta-list-impl';
import { NavigationList } from '@app/utils/lists/navigation-list/navigation-list';
import { NavigationListImpl } from '@app/utils/lists/navigation-list/navigation-list-impl';
import { isBlank, isNotBlank } from '@app/utils/string';
import { StringMap } from '@app/utils/string-map';
import { BootstrapViewport } from '@app/utils/viewport';
import { NgSelectComponent } from '@ng-select/ng-select';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';

@UntilDestroy()
@Component({
  selector: 'portal-filter-input',
  templateUrl: './filter-input.component.html',
  styles: [],
  standalone: false,
})
export class FilterInputComponent<T> implements FocusableElement, OnChanges, OnInit {
  @ViewChild('container', { static: false })
  public container: ElementRef<HTMLDivElement>;

  @ViewChild('firstLevel', { static: false })
  public firstLevel: TippyDirective;

  @ViewChild('filter', { static: false })
  public input: NgSelectComponent;

  @ViewChild('secondLevel', { static: false })
  public secondLevel: TippyDirective;

  @Input()
  public calculateMetaData!: (model: T) => FilterableEntry;

  @Input()
  public countMode: 'dynamic' | 'static' = 'static';

  @Input()
  public entries: T[];

  @HostBinding('class.filter-input--in-table')
  public _inTable: boolean = false;

  @Input()
  public set inTable(value: undefined | boolean) {
    this._inTable = value !== false;
  }

  @Input()
  public properties?: MetaDataProperty[] = [];

  @Input()
  public value: string;

  @Output()
  public readonly changed: EventEmitter<T[]> = new EventEmitter();

  @Output()
  public readonly valueChanged: EventEmitter<string> = new EventEmitter();

  public readonly handleInput = debounce((event: { term: string; items: any[] }) => {
    this.updateFilter(event.term);
  }, 400);

  public body = document.body;
  public error: string | null = null;
  public filterValue: string = '';
  public hasError: boolean = false;
  public isFiltered: boolean = false;
  public hasFocus: boolean = false;
  public metaList: MetaList<T>;
  public propertyMap: StringMap<MetaDataProperty> = {};
  public tags: TypeaheadEntry<Term>[] = [];
  public firstLevelList!: NavigationList<MetaDataProperty>;
  public secondLevelList!: NavigationList<SecondLevelEntry<T>>;

  public compare = (a: TypeaheadEntry<Term>, b: TypeaheadEntry<Term>) => a.key === b.key;

  get hasProperties(): boolean {
    return this.properties != null && this.properties.length > 0;
  }

  constructor(private translateService: TranslateService) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.metaList !== undefined && ('entries' in changes || 'properties' in changes)) {
      this.metaList
        .updateEntries(changes.entries?.currentValue)
        .updateProperties(changes.properties?.currentValue);

      if (isNotBlank(this.value)) {
        // If value is defined at this place (meaning this is the first time that
        // entries are available), then we will update the filter and set the value to
        // ''. Otherwise, the filter would be applied again, whenever the data changes.
        this.metaList.updateFilter(this.value);
        this.value = '';
      }

      this.metaList.draw();
    }

    if ('value' in changes && this.metaList != null) {
      this.metaList.updateFilter(changes.value.currentValue).draw();
      this.updateFilterInput(changes.value.currentValue);

      this.value = '';
    }

    if ('properties' in changes && changes.properties.currentValue != null) {
      this.propertyMap = toMap(changes.properties.currentValue, 'key');
    }
  }

  ngOnInit() {
    this.metaList = new MetaListImpl<T>(this.translateService, {
      calculateMetaData: this.calculateMetaData,
      countMode: this.countMode === 'static' ? MetaListCountMode.Static : MetaListCountMode.Dynamic,
    });

    this.firstLevelList = new NavigationListImpl();
    this.secondLevelList = new NavigationListImpl();

    this.firstLevelList.active$.pipe(untilDestroyed(this)).subscribe((active) => {
      this.updateSecondLevelEntries(active);
    });

    this.metaList.error$.pipe(untilDestroyed(this)).subscribe((error) => {
      if (error != null) {
        this.error = error.message;
        this.hasError = true;
      } else {
        this.error = null;
        this.hasError = false;
      }
    });

    this.metaList.filter$.pipe(untilDestroyed(this)).subscribe((filterValue) => {
      this.filterValue = filterValue;
      this.isFiltered = isNotBlank(filterValue);

      if (isBlank(filterValue)) {
        this.tags = [];
      } else {
        const result = parse(filterValue);

        if (isPositiveParse(result)) {
          this.tags = result.terms
            .filter((term) => term.operator !== 'match')
            .map((term) => {
              const key = term.arguments.length === 1 ? term.key + term.arguments.join('') : term.key;
              return {
                key: key,
                label: termToString(term),
                value: term,
              };
            });
        }
      }

      this.valueChanged.emit(filterValue);
    });

    this.metaList.items$.pipe(untilDestroyed(this)).subscribe((items) => {
      this.changed.emit(items);
    });

    this.metaList.properties$.pipe(untilDestroyed(this)).subscribe((properties) => {
      this.firstLevelList.setEntries(properties).draw();
    });

    this.translateService.onLangChange.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.properties != null) {
        this.input.handleClearClick();
        this.metaList.clearFilter().refresh().draw();
      }
    });

    this.metaList.updateEntries(this.entries ?? []).updateProperties(this.properties);

    if (this.properties != null) {
      this.metaList.updateFilter(this.value);
      this.metaList.draw();
    }

    if (this.value != null) {
      this.updateFilterInput(this.value);
    }
  }

  public activeCount(entry: SecondLevelEntry<T>): number {
    return this.metaList.keyCount(entry.key);
  }

  public clearFilter(): void {
    this.metaList.clearFilter().draw();
  }

  public focus(): void {
    this.focusInput();
  }

  public focusInput(): void {
    this.input.focus();
  }

  public handleArrowDown(event: Event) {
    if (this.secondLevel.isVisible) {
      this.navigateSecondLevel(1, event);
    } else {
      this.navigateFirstLevel(1, event);
    }
  }

  public handleArrowUp(event: Event) {
    if (this.secondLevel.isVisible) {
      this.navigateSecondLevel(-1, event);
    } else {
      this.navigateFirstLevel(-1, event);
    }
  }

  public handleBlur() {}

  public handleCtrlSpace() {
    this.firstLevelList.highlight(0).draw();

    this.showFirstLevel();
  }

  public handleEnter(event: Event) {
    if (this.secondLevel.isVisible) {
      this.toggleEntry(this.secondLevelList.highlighted.key);
    } else {
      if (this.prevent(event)) {
        this.updateSecondLevelEntries(this.firstLevelList.highlighted);

        setTimeout(() => {
          this.secondLevel.show();
        }, 0);
      }
    }
  }

  public handleEscape(event: Event) {
    if (this.prevent(event)) {
      if (this.secondLevel.isVisible) {
        this.secondLevel.hide();
      } else {
        this.firstLevel.hide();
      }
    }
  }

  public handleFilterButtonClick() {
    this.focusInput();

    this.showFirstLevel();
  }

  public handleFirstLevelMouseEnter(entry: MetaDataProperty): void {
    this.firstLevelList.highlight(entry).draw();

    this.updateSecondLevelEntries(entry);

    requestAnimationFrame(() => {
      this.secondLevel.show();
    });
  }

  public handleSecondLevelMouseEnter(entry: SecondLevelEntry<T>) {
    this.secondLevelList.highlight(entry).draw();
  }

  public handleFirstLevelTriggered(): void {
    this.firstLevelList.draw();
  }

  /**
   * If the current viewport is 'xs' or 'sm' then the first level will be shown automatically
   */
  public handleFocus() {
    if (BootstrapViewport.is('xs') || BootstrapViewport.is('sm')) {
      this.firstLevel.show();
    }

    this.hasFocus = true;
  }

  public handleKeyDown = (event: KeyboardEvent): boolean => {
    let handled: boolean = false;

    if (event.key === Key.Enter) {
      handled = true;

      this.handleEnter(event);
    } else if (event.key === Key.ArrowDown) {
      handled = true;

      this.handleArrowDown(event);
    } else if (event.key === Key.ArrowUp) {
      handled = true;

      this.handleArrowUp(event);
    } else if (event.key === Key.Escape) {
      handled = true;

      this.handleEscape(event);
    } else if (event.ctrlKey === true && (event.key === ' ' || event.key === 'Spacebar')) {
      handled = true;

      this.handleCtrlSpace();
    }

    return !handled;
  };

  public handleTagRemoved(term: Term): void {
    for (const argument of term.arguments) {
      const metaKey = `${term.key}:${argument}`;

      this.metaList.deactivate(metaKey);
    }

    this.metaList.draw();
  }

  public isActive(entry: SecondLevelEntry<T>): boolean {
    return this.metaList.isKeyActive(entry.key);
  }

  public label(args: string[]): string {
    return args.join(', ');
  }

  public property(key: string): MetaDataProperty | undefined {
    return this.propertyMap[key];
  }

  /**
   * Reset the first and second level list
   */
  public reset(): void {
    this.firstLevelList.reset().draw();

    this.secondLevelList.reset().setEntries([]).draw();
  }

  public toggleEntry(entry: string) {
    this.metaList.toggle(entry).draw();
  }

  /**
   * Perform a navigation operation on the first level menu
   * @param direction Go in this direction
   * @param event Prevent this event if the first level menu is visible
   */
  private navigateFirstLevel(direction: number, event: Event) {
    if (this.prevent(event)) {
      this.firstLevelList.step(direction).draw();
    }
  }

  /**
   * Perform a navigation operation on the second level menu
   * @param direction Go in this direction
   * @param event Prevent this event if the second level menu is visible
   */
  private navigateSecondLevel(direction: number, event: Event) {
    if (this.prevent(event)) {
      this.secondLevelList.step(direction).draw();
    }
  }

  /**
   * Prevent the event only if the first level is visible
   */
  private prevent(event: Event): boolean {
    let prevented = false;

    if (this.firstLevel.isVisible) {
      event.preventDefault();

      prevented = true;
    }

    return prevented;
  }

  private showFirstLevel(): void {
    this.firstLevel.show();

    if (this.properties?.length === 1) {
      this.firstLevelList.activate(0).highlight(0).draw();
      this.secondLevel.show();
    }
  }

  private updateFilter(value: string): void {
    if (this.entries != null) {
      this.metaList.updateFilter(value).draw();
    }

    this.valueChanged.emit(value);
  }

  private updateFilterInput(value: string): void {
    setTimeout(() => {
      this.input.filter(value);
    });
  }

  private updateSecondLevelEntries(active: MetaDataProperty) {
    const entries: SecondLevelEntry<T>[] = active.values.map((value: string) => {
      let key = `${active.key}:${value}`;

      return {
        key,
        label: value,
        value: this.metaList.getEntry(key),
      };
    });

    if (active.sortByLabel) {
      this.secondLevelList.sortBy((a, b) =>
        this.translateService.instant(a.label).localeCompare(this.translateService.instant(b.label))
      );
    } else {
      this.secondLevelList.sortBy(undefined);
    }

    this.secondLevelList.reset().setEntries(entries).highlight(0).draw();
  }
}
